import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { MarketingPage } from "@/bryntum-lib/models/MarketingPage";
import { Site } from "@/bryntum-lib/models/Site";
import { useSite } from "../hooks/useSite";

interface MarketingPagesContextValue {
  selectedPage: MarketingPage | null;
  toggleSelectedPage: (page: MarketingPage) => void;
  clearSelectedPage: () => void;
  siteRecord: Site | null;
  ensureSiteRecord: () => Site | null;
  isPublishing: boolean;
  isRepublishing: boolean;
  startPublish: () => Promise<boolean>;
  includedPageCount: number;
}

const MarketingPagesContext = createContext<MarketingPagesContextValue | undefined>(undefined);

export function MarketingPagesProvider({ children }: { children: ReactNode }) {
  const { project } = useSwivuBryntumContext();
  const [selectedPageId, setSelectedPageId] = useState<number | null>(null);
  const site = useSite();

  const { version: pagesVersion } = useSwivuStore({ storeId: STORE_KEYS.marketingPageStore });

  const pages = useMemo<MarketingPage[]>(() => {
    if (!project) return [];
    const store = project.getCrudStore(STORE_KEYS.marketingPageStore);
    if (!store) return [];
    return store.query(() => true) as unknown as MarketingPage[];
  }, [project, pagesVersion]);

  const selectedPage = useMemo(
    () => pages.find((p) => p.id === selectedPageId) ?? null,
    [pages, selectedPageId],
  );

  const toggleSelectedPage = useCallback((page: MarketingPage) => {
    setSelectedPageId((prev) => (prev === page.id ? null : page.id));
  }, []);

  const clearSelectedPage = useCallback(() => setSelectedPageId(null), []);

  const value = useMemo(
    () => ({
      selectedPage,
      toggleSelectedPage,
      clearSelectedPage,
      siteRecord: site.siteRecord,
      ensureSiteRecord: site.ensureSiteRecord,
      isPublishing: site.isPublishing,
      isRepublishing: site.isRepublishing,
      startPublish: site.startPublish,
      includedPageCount: site.includedPageCount,
    }),
    [
      selectedPage,
      toggleSelectedPage,
      clearSelectedPage,
      site.siteRecord,
      site.ensureSiteRecord,
      site.isPublishing,
      site.isRepublishing,
      site.startPublish,
      site.includedPageCount,
    ],
  );

  return (
    <MarketingPagesContext.Provider value={value}>{children}</MarketingPagesContext.Provider>
  );
}

export function useMarketingPagesContext() {
  const ctx = useContext(MarketingPagesContext);
  if (!ctx) {
    throw new Error("useMarketingPagesContext must be used within MarketingPagesProvider");
  }
  return ctx;
}
